/**
 * « Ta journée » (V2 §6.2): the generative summary of the day (GET /api/pulse/summary) as an agent's bubble at the top of
 * today's conversation — its title in Newsreader, then a few « heure · quoi » rows — and the full brief one tap away, in
 * a sheet.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { Brief } from "../../shared/contracts";
import { useDashboard, usePulseSummary } from "../routes/pulse";
import { BriefView } from "./brief";
import { IconX } from "./icons";
import { PulseSummarySkeleton, PulseSummaryView } from "./pulse-summary";
import { ROUND_BUTTON } from "./ui";

export function DayBrief() {
  const summary = usePulseSummary();
  const dash = useDashboard();
  const [full, setFull] = useState(false);
  const titleId = useId();
  const brief = dash.data?.latestBrief ?? null;
  return (
    <section aria-labelledby={titleId} data-testid="day-brief" className="min-w-0">
      <h2 id={titleId} className="mb-1.5 font-display text-[18px] leading-tight">
        Ta journée
      </h2>
      {summary.data ? (
        <PulseSummaryView summary={summary.data} />
      ) : summary.loading ? (
        <PulseSummarySkeleton />
      ) : (
        <button type="button" onClick={() => void summary.reload()} className="inline-flex min-h-8 items-center text-[14px] text-muted underline underline-offset-[3px] max-md:min-h-11">
          Réessayer
        </button>
      )}
      {brief ? (
        <button type="button" onClick={() => setFull(true)} className="mt-2 inline-flex min-h-8 items-center text-[13.5px] text-muted underline underline-offset-[3px] hover:text-fg max-md:min-h-11">
          Le brief complet
        </button>
      ) : null}
      {full && brief ? <BriefSheet brief={brief} onClose={() => setFull(false)} /> : null}
    </section>
  );
}

/** The full brief, in a sheet over the conversation. */
function BriefSheet({ brief, onClose }: { brief: Brief; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 h-full w-full bg-[var(--dim)]" />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Le brief complet"
        className="sheet-up relative max-h-[88dvh] w-full max-w-[640px] overflow-y-auto rounded-t-[26px] bg-elev px-5 pt-5 pb-[calc(env(safe-area-inset-bottom)+24px)] outline-none md:rounded-[26px] md:pb-6"
      >
        <button type="button" aria-label="Fermer" onClick={onClose} className={`${ROUND_BUTTON} float-right`}>
          <IconX width={14} height={14} strokeWidth={2.6} />
        </button>
        <BriefView brief={brief} headingLevel={2} />
      </div>
    </div>
  );
}
